import React, { Fragment } from "react";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaPaperPlane,
  FaHandHoldingUsd,
  FaUnlink,
} from "react-icons/fa";
import { AppLink } from "../../shared";
import FirmBusyBadge from "../FirmBusyBadge";
import { getFirmTypeStyle } from "../../../utils/firmTypeStyles";
import { getFirmIcon } from "../../../utils/firmUtils";

// Sticky header hücre stili — DevicesTable ile birebir aynı (dikey scroll
// tablonun kendi kabuğunda olduğu için thead th'leri sticky çalışır;
// arka plan şart, satırlar altından geçer).
// Kota sütununun kolon şablonu — BAŞLIK ve HÜCRE aynı şablonu kullanır,
// yoksa "FİRMADA | CİHAZLARDA" etiketleri sayıların üstüne denk gelmiyor
// (başlık tek parça metinken sola yapışıyordu). Sayı sütunları 7rem: hem
// 1.000.000 hem "CİHAZLARDA" başlığı sığar.
const QUOTA_GRID_COLS = "grid-cols-[13rem_7rem_0.5rem_7rem]";

const TH_CLASS =
  "sticky top-0 z-10 bg-gray-100 border-b border-gray-200 px-4 py-3 text-xs font-bold text-gray-500 tracking-wide whitespace-nowrap";

/**
 * Firma > Alt Firmalar sekmesi — kart grid'inin yerini alan sütunlu tablo.
 * Cihazlar sekmesindeki `DevicesTable` ile aynı kalıp: kenar boşluksuz
 * (full-bleed) yerleşir, başlık satırı sabittir, liste onun altında kayar,
 * dar ekranda tablo yerine dikey kartlara döner.
 *
 * NAVIGASYON YALNIZCA IKON + FIRMA ADI (kullanıcı kararı 2026-08-28).
 * Satırın tamamını kaplayan görünmez link yok — kota hücresine ya da boş
 * alana dokunmak sayfa değiştirmez.
 */
const SubFirmsTable = ({
  firms = [],
  canNavigate = false,
  canSendQuota = false,
  canUnlink = false,
  onSendQuota,
  onTakeBackQuota,
  onUnlink,
  // Kota butonlarını satır bazında açar/kapar (ör. Müşterilerim'de yalnız
  // doğrudan müşteriler). Verilmezse canSendQuota tüm satırlara uygulanır.
  canSendQuotaFor,
  // Kilidi tutulan (işlemi süren) firma id'leri (#303) — rozet + kota kilidi.
  busyIds,
  // Bağı kopar düğmesini satır bazında açar/kapar (bayide tek Müşteriler
  // sekmesi: alt bayi ve müşteri satırları farklı izin/uç kullanır, #376).
  canUnlinkFor,
  // Satırda "Alt Bayi" / "Müşteri" tip rozeti (#376)
  showTypeBadge = false,
}) => {
  const isBusy = (subFirm) => !!busyIds && busyIds.has(String(subFirm._id));
  const rowCanSendQuota = (subFirm) =>
    canSendQuota && (canSendQuotaFor ? canSendQuotaFor(subFirm) : true);
  const rowCanUnlink = (subFirm) =>
    canUnlink && (canUnlinkFor ? canUnlinkFor(subFirm) : true);
  const typeBadgeLabel = (subFirm) =>
    subFirm.firmType === "dealer" ? "Alt Bayi" : "Müşteri";
  const getProducts = (subFirm) =>
    Array.isArray(subFirm.productInfo?.productInfo)
      ? subFirm.productInfo.productInfo.filter((p) => p.quotaDistribution)
      : [];

  const getLocation = (subFirm) =>
    subFirm.address?.city && subFirm.address?.town
      ? `${subFirm.address.city} / ${subFirm.address.town}`
      : subFirm.address?.city || subFirm.address?.town || "";

  /** İkon + firma adı — tek navigasyon hedefi; yetki yoksa düz metin. */
  const FirmIdentity = ({ subFirm, compact = false }) => (
    <div className="flex items-center gap-3 min-w-0">
      {canNavigate ? (
        <AppLink
          to={`/Anasayfa/Firma/${subFirm._id}`}
          className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-lg border flex items-center justify-center shrink-0 transition hover:brightness-95 ${getFirmTypeStyle(subFirm).iconBox}`}
          aria-label={`${subFirm.firmName} firmasına git`}
        >
          {getFirmIcon(subFirm, "w-3.5 h-3.5")}
        </AppLink>
      ) : (
        <div
          className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-lg border flex items-center justify-center shrink-0 ${getFirmTypeStyle(subFirm).iconBox}`}
        >
          {getFirmIcon(subFirm, "w-3.5 h-3.5")}
        </div>
      )}
      <div className="min-w-0">
        {canNavigate ? (
          <AppLink
            to={`/Anasayfa/Firma/${subFirm._id}`}
            className="group/nav flex items-center gap-1.5 min-w-0 w-fit max-w-full"
          >
            <span className="text-sm text-gray-900 truncate group-hover/nav:text-blue-600 transition-colors">
              {subFirm.firmName}
            </span>
            <FaExternalLinkAlt className="w-2.5 h-2.5 text-gray-300 group-hover/nav:text-blue-500 transition-colors shrink-0" />
          </AppLink>
        ) : (
          <span className="block text-sm text-gray-900 truncate">
            {subFirm.firmName}
          </span>
        )}
        <p className="flex items-center gap-1.5 text-[11px] text-gray-500 min-w-0">
          <span className="truncate">Bayser No: {subFirm.bayserNo || "-"}</span>
          {showTypeBadge && (
            <span className={`shrink-0 rounded-md border px-1.5 py-px text-[10px] font-SemiBold ${getFirmTypeStyle(subFirm).badge}`}>
              {typeBadgeLabel(subFirm)}
            </span>
          )}
        </p>
        {/* Pasif firma işaretlenir (#205) — Genel Firmalar'daki rozetle aynı dil. */}
        {subFirm.isActive === false && (
          <span className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-md text-[11px] font-SemiBold bg-red-50 text-red-600">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Pasif
          </span>
        )}
        {isBusy(subFirm) && <FirmBusyBadge className="mt-1" />}
      </div>
    </div>
  );

  /** Firmanın gerçek cihaz adedi (#376) — sunucudaki firma `deviceCount`'u */
  const DeviceCount = ({ subFirm }) =>
    subFirm.deviceCount == null ? (
      <span className="text-xs text-gray-300">-</span>
    ) : (
      <span className="text-sm font-Bold tabular-nums text-gray-800">
        {Number(subFirm.deviceCount).toLocaleString("tr-TR")}
      </span>
    );

  /** Ürün başına kota satırı — Cihazlar tablosundaki KOTA hücresiyle aynı dil. */
  /**
   * Ürün başına TEK SATIR:
   *   Ürün Adı   firmada (havuz) | cihazlarda
   *
   * "Dağıtılan" dili kaldırıldı (kullanıcı kararı 2026-10-09, #300). Cihazlarda
   * = firmanın TÜM cihazlarındaki kota (`quota`; dağıtımı kapalı ürün dahil).
   *
   * SABİT SÜTUN GENİŞLİĞİ (kullanıcı kararı 2026-08-28): `auto` sütunlarla
   * grid yalnızca TEK firmanın kendi hücresinde hizalıyordu; her tablo satırı
   * ayrı bir grid olduğu için firmadan firmaya kayıyordu. Genişlikler sabit
   * verilince hizalama tüm listede ortak olur.
   *
   * Ad sütunu "Türk Kahvesi (0 cihaz)" gibi en uzun gerçek etiketi rahat
   * alacak ölçüde (13rem); daha uzun bir ad gelirse `truncate` ile kısalır —
   * taşıp sayıların üstüne binmesindense kısalması yeğdir. Sayı sütunları
   * 1.000.000 (kota tavanı) sığacak genişlikte.
   *
   * Etiketler satıra değil SÜTUN BAŞLIĞINA yazılır ("KOTA · FİRMADA |
   * CİHAZLARDA") — her satırda tekrar edilince liste iki katına çıkıyordu.
   */
  const QuotaLines = ({ subFirm }) => {
    const products = getProducts(subFirm);
    if (products.length === 0) {
      return <span className="text-xs text-gray-300">-</span>;
    }
    return (
      <div className={`grid ${QUOTA_GRID_COLS} gap-x-2 gap-y-1 items-baseline`}>
        {products.map((p, idx) => (
          <Fragment key={idx}>
            {/* Ürün başına cihaz sayısı yazılmaz: 3'ü bir arada tek makine her
                üründe 1 sayılıyordu. Gerçek adet CİHAZLAR sütununda (#376). */}
            <span className="text-sm text-gray-700 truncate">{p.productName}</span>
            <span
              className="text-sm text-right font-Bold text-indigo-600 tabular-nums"
              title="Firmada"
            >
              {(p.poolQuota ?? 0).toLocaleString("tr-TR")}
            </span>
            <span className="text-sm text-gray-300 text-center">|</span>
            <span
              className="text-sm text-right font-Bold text-emerald-600 tabular-nums"
              title="Cihazlarda"
            >
              {(Number(p.quota) || 0).toLocaleString("tr-TR")}
            </span>
          </Fragment>
        ))}
      </div>
    );
  };

  /**
   * İki sütun: solda kota butonları ALT ALTA, sağda bağı kopar KARE
   * (kullanıcı kararı 2026-08-28).
   *
   * Kare buton `self-stretch` + `aspect-square` ile kurulur: yüksekliği
   * soldaki iki butonun toplamı kadar olur, genişliği de ona eşitlenir.
   * Sabit piksel vermek buton yüksekliği/punto değişince bozulurdu.
   */
  const ActionButtons = ({ subFirm, full = false }) => {
    const showQuota = rowCanSendQuota(subFirm);
    const showUnlink = rowCanUnlink(subFirm);
    if (!showQuota && !showUnlink) return null;
    const busy = isBusy(subFirm);

    const quotaBtn =
      "inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-SemiBold border transition-colors whitespace-nowrap";

    return (
      <div className={`flex items-stretch gap-2 ${full ? "" : "justify-end"}`}>
        {showQuota && (
          <div className={`flex flex-col gap-1.5 ${full ? "flex-1" : ""}`}>
            <button
              type="button"
              onClick={() => onSendQuota?.(subFirm)}
              disabled={busy}
              className={`${quotaBtn} bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed`}
              title={busy ? "Firmada işlem sürüyor" : `${subFirm.firmName} firmasına kota ver`}
            >
              <FaPaperPlane className="w-3.5 h-3.5" /> Kota Ver
            </button>
            {/* Ters yön: alt firmadaki kotayı üst firmaya geri çeker.
                Aynı izne bağlı — iki yön de aynı transfer mekanizması. */}
            <button
              type="button"
              onClick={() => onTakeBackQuota?.(subFirm)}
              disabled={busy}
              className={`${quotaBtn} bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed`}
              title={busy ? "Firmada işlem sürüyor" : `${subFirm.firmName} firmasından kota geri al`}
            >
              <FaHandHoldingUsd className="w-3.5 h-3.5" /> Kota Geri Al
            </button>
          </div>
        )}

        {showUnlink && (
          <button
            type="button"
            onClick={() => onUnlink?.(subFirm)}
            className={`self-stretch shrink-0 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-red-50 text-red-500 border border-red-100 hover:bg-red-100 transition-colors ${
              // Kota butonları varsa dikey (ikon üstte, iki satır metin);
              // yoksa tek satırlık yatay buton. `aspect-square` KALDIRILDI:
              // genişliği yüksekliğe eşitlediği için metin kenarlara
              // yapışıyordu — genişlik artık padding + metne göre oluşur.
              showQuota ? "flex-col" : "flex-row"
            }`}
            title={`${subFirm.firmName} firmasının bağını kopar`}
          >
            <FaUnlink className="w-4 h-4 shrink-0" />
            {/* İki kelime ALT ALTA (kullanıcı isteği): kare butonda tek satır
                halinde taşıyordu. */}
            <span className="flex flex-col items-center text-[10px] font-SemiBold leading-tight">
              <span>Bağı</span>
              <span>Kopar</span>
            </span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto bg-white border-t border-gray-200">
      {/* Mobil kart görünümü — dar ekranda tablo yerine dikey kartlar */}
      <div className="sm:hidden divide-y divide-gray-100">
        {firms.map((subFirm) => {
          const location = getLocation(subFirm);
          return (
            <div key={subFirm._id} className="px-3.5 py-3 space-y-2">
              <FirmIdentity subFirm={subFirm} compact />

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
                {subFirm.email && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <FaEnvelope className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                    <span className="truncate">{subFirm.email}</span>
                  </span>
                )}
                {subFirm.tel && (
                  <span className="inline-flex items-center gap-1">
                    <FaPhone className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                    {subFirm.tel}
                  </span>
                )}
                {location && (
                  <span className="inline-flex items-center gap-1">
                    <FaMapMarkerAlt className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                    {location}
                  </span>
                )}
              </div>

              {subFirm.deviceCount != null && (
                <p className="text-[11px] text-gray-600">
                  Cihaz: <span className="font-Bold text-gray-800">{subFirm.deviceCount}</span>
                </p>
              )}
              <QuotaLines subFirm={subFirm} />

              {(canSendQuota || canUnlink) && (
                <ActionButtons subFirm={subFirm} full />
              )}
            </div>
          );
        })}
      </div>

      <table className="hidden sm:table w-full min-w-[1024px] text-sm">
        <thead>
          <tr>
            <th className={`${TH_CLASS} text-left`}>FİRMA</th>
            {/* İletişim ve konum tek sütunda (kullanıcı isteği 2026-09-01):
                ikisi de kısa satırlar, ayrı sütunda gereksiz yer kaplıyordu. */}
            <th className={`${TH_CLASS} text-left`}>İLETİŞİM VE KONUM</th>
            <th className={`${TH_CLASS} text-right`}>CİHAZLAR</th>
            <th className={`${TH_CLASS} text-left`}>
              <div className={`grid ${QUOTA_GRID_COLS} gap-x-2 items-baseline`}>
                <span>KOTA</span>
                <span className="text-right">FİRMADA</span>
                <span className="text-center text-gray-300">|</span>
                <span className="text-right">CİHAZLARDA</span>
              </div>
            </th>
            {(canSendQuota || canUnlink) && (
              <th className={`${TH_CLASS} text-right pr-6`}>İŞLEM</th>
            )}
          </tr>
        </thead>
        <tbody>
          {firms.map((subFirm) => {
            const location = getLocation(subFirm);
            return (
              <tr
                key={subFirm._id}
                className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors"
              >
                <td className="px-4 py-3 align-middle">
                  <FirmIdentity subFirm={subFirm} />
                </td>

                {/* İletişim + konum tek hücrede, alt alta */}
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-col gap-1">
                    {subFirm.email && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                        <FaEnvelope className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate max-w-[220px]">
                          {subFirm.email}
                        </span>
                      </span>
                    )}
                    {subFirm.tel && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                        <FaPhone className="w-3 h-3 text-gray-400 shrink-0" />
                        {subFirm.tel}
                      </span>
                    )}
                    {location && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                        <FaMapMarkerAlt className="w-3 h-3 text-gray-400 shrink-0" />
                        {location}
                      </span>
                    )}
                    {!subFirm.email && !subFirm.tel && !location && (
                      <span className="text-xs text-gray-300">-</span>
                    )}
                  </div>
                </td>

                <td className="px-4 py-3 align-middle text-right">
                  <DeviceCount subFirm={subFirm} />
                </td>

                <td className="px-4 py-3 align-middle">
                  <QuotaLines subFirm={subFirm} />
                </td>

                {/* pr-6: aksiyon şeridi tablonun sağ kenarına yapışmasın */}
                {(canSendQuota || canUnlink) && (
                  <td className="px-4 pr-6 py-3 align-middle text-right">
                    <ActionButtons subFirm={subFirm} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SubFirmsTable;
